import Dialog from '@material-ui/core/Dialog';
import React, {
  MouseEventHandler,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useMediaQuery } from 'common/utils';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';
import styles from './CoursePaymentDialog.module.scss';
import Icon from 'components/common/Icon';
import { CourseDetailsContext } from '..';
import { useQuery } from 'react-query';
import axios from 'axios';
import { GetSubscriptionInfoResult } from 'common/interfaces/api';
import InputCard from './InputCard';
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import CoursePreview from './CoursePreview';
import PolicyDescription from './PolicyDescription';
import { buyCourse } from 'service/course';
import { track } from '@amplitude/analytics-browser';
import {
  checkPaymentHistoryTeenage,
  getInitCreditIntent,
  setPaymentMethod,
} from 'service/payment';
import TeenagePaymentAlertPopup from 'components/common/TeenagePaymentAlertPopup';
import { CHECK_3DS_CODE } from 'common/utils/payment';

export default function CoursePaymentDialog(): JSX.Element {
  const isMobile = useMediaQuery('(max-width: 875px)');
  const { courseInfo, openCoursePaywall, setOpenCoursePaywall, refetchCourse } =
    useContext(CourseDetailsContext);
  const currentUser = useStore((state) => state.currentUser);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage
  );
  const [stripe3DSUrl, setStripe3DSUrl] = useState<string>();
  const [teenAgePaymentErrorReason, setTeenAgePaymentErrorReason] =
    useState<number>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [name, setName] = useState<string>('');
  const [cardError, setCardError] = useState<string>(null);
  const [expiryError, setExpiryError] = useState<string>(null);
  const [cvcError, setCvcError] = useState<string>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);
  const { isLoading, data } = useQuery(
    ['getSubscriptionInfo'],
    async () => {
      if (!isAuthInit || !currentUser) return Promise.reject();
      return (await axios.get('/users/accounts/subscription_info'))
        .data as GetSubscriptionInfoResult;
    },
    {
      enabled: isAuthInit && !!currentUser,
    }
  );
  const paymentMethod = data?.data?.payment_method;
  const stripe = useStripe();
  const elements = useElements();
  const isValidInput =
    paymentMethod ||
    (name &&
      !cardError &&
      !expiryError &&
      !cvcError &&
      cardComplete &&
      expiryComplete &&
      cvcComplete);

  // initCreditCard
  const initCreditCard = async () => {
    try {
      const getIntentResponse = await getInitCreditIntent();
      const cardNumberElement = elements.getElement(CardNumberElement);
      const payment_method = paymentMethod
        ? paymentMethod.id
        : {
            card: cardNumberElement,
            billing_details: {
              name: name,
            },
          };
      const { setupIntent } = await stripe.confirmCardSetup(
        getIntentResponse.data.client_secret,
        {
          payment_method,
        }
      );
      if (!setupIntent || setupIntent.status !== 'succeeded') {
        throw new Error('Failed to create payment method');
      }
      track('Add Credit Card Success', {
        screen: 'course_payment_dialog',
      });
      await setPaymentMethod(setupIntent.payment_method);
    } catch (e) {
      track('Create Payment Method Failed', {
        screen: 'course_payment_dialog',
      });
      setIsProcessing(false);
      setSnackbarMessage({
        type: 'error',
        text: 'カードが登録できませんでした',
      });
      throw new Error('Failed to create payment method');
    }
  };

  const onCompleteBuyCourse = () => {
    track('Buy Course', {
      course_id: courseInfo.id?.toString(),
      course_name: courseInfo.title,
    });
    setSnackbarMessage({
      type: 'success',
      text: 'コースを購入しました',
    });
    // need time for sync state of stripe and backend
    setTimeout(function () {
      refetchCourse();
      handleClose();
    }, 3000);
  };

  // callback when failed subscription
  const onFailedBuyCourse = () => {
    setIsProcessing(false);
    setSnackbarMessage({
      type: 'error',
      text: 'コースの購入に失敗しました',
    });
  };

  const handleBuyCourse: MouseEventHandler = async (e) => {
    e.preventDefault();
    if (isProcessing) return;
    setIsProcessing(true);
    if (!currentUser?.is_adult) {
      try {
        const checkTeenagePaymentResponse = await checkPaymentHistoryTeenage({
          price_amount: courseInfo.web_amount,
        });
        const isAbleToPay = checkTeenagePaymentResponse?.data?.is_purchasable;
        if (!isAbleToPay && checkTeenagePaymentResponse?.data?.reason) {
          track('Show Underage Payment Error', {
            type: 'course',
          });
          setTeenAgePaymentErrorReason(
            checkTeenagePaymentResponse?.data?.reason
          );
          return;
        }
      } catch (e) {
        //
      }
    }
    try {
      if (!paymentMethod) {
        await initCreditCard();
      }
      try {
        const buyCourseResponse = await buyCourse(courseInfo.id);
        if (buyCourseResponse?.confirm_response?.confirm_url) {
          setStripe3DSUrl(buyCourseResponse.confirm_response.confirm_url);
        } else {
          onCompleteBuyCourse();
        }
      } catch (e) {
        onFailedBuyCourse();
        throw new Error('Failed to create buy course request');
      }
    } catch (e) {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (!stripe3DSUrl) return;
    const handle3DSMessage = async (event: MessageEvent<string>) => {
      if (event.data === CHECK_3DS_CODE) {
        try {
          const urlParams = new URLSearchParams(stripe3DSUrl);
          const clientSecret = urlParams.get('payment_intent_client_secret');
          await stripe
            .retrievePaymentIntent(clientSecret)
            .then(function (result) {
              if (result.error) {
                track('Onetime Payment With 3DS', {
                  type: 'course',
                  status: 'failed',
                });
                onFailedBuyCourse();
              } else {
                if (result.paymentIntent.status === 'succeeded') {
                  // Show your customer that the payment has succeeded
                  onCompleteBuyCourse();
                  track('Onetime Payment With 3DS', {
                    type: 'course',
                    status: 'succeeded',
                  });
                } else if (
                  result.paymentIntent.status === 'requires_payment_method'
                ) {
                  onFailedBuyCourse();
                  track('Onetime Payment With 3DS', {
                    type: 'course',
                    status: 'failed',
                  });
                }
              }
              setStripe3DSUrl(null);
            });
        } catch (e) {
          console.log('Error', e);
          onFailedBuyCourse();
          setStripe3DSUrl(null);
        }
      }
    };
    window.addEventListener('message', handle3DSMessage);
    return () => window.removeEventListener('message', handle3DSMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe3DSUrl]);

  // Check input
  const handleCardNumberChange = (e: StripeCardNumberElementChangeEvent) => {
    setCardError(e.error ? e.error.message : null);
    setCardComplete(e.complete);
  };
  const handleCardExpiryChange = (e: StripeCardExpiryElementChangeEvent) => {
    setExpiryError(e.error ? e.error.message : null);
    setExpiryComplete(e.complete);
  };
  const handleCardCvcChange = (e: StripeCardCvcElementChangeEvent) => {
    setCvcError(e.error ? e.error.message : null);
    setCvcComplete(e.complete);
  };

  const paymentInfo = () => {
    return (
      <div className={styles.formSection}>
        {isLoading ? (
          <div className={styles.loading}>
            <Icon name="loading-anim-3" width={24} height={24} />
          </div>
        ) : (
          <>
            <div className={styles.title}>
              {courseInfo.user?.name}さんの{'\n'}コースを購入する
            </div>
            <div className={styles.discount}>
              <span className={styles.name}>{courseInfo.title}</span>
              <span className={styles.line}></span>
              <div className={styles.price}>
                <span className={styles.number}>
                  {courseInfo.web_amount?.toLocaleString()}円
                </span>
              </div>
            </div>

            {!paymentMethod ? (
              <InputCard
                setName={setName}
                name={name}
                handleCardCvcChange={handleCardCvcChange}
                handleCardExpiryChange={handleCardExpiryChange}
                handleCardNumberChange={handleCardNumberChange}
              />
            ) : (
              <div className={styles.existingCard}>
                <div className={styles.ccNumber}>
                  **** **** **** {paymentMethod.last4}
                </div>
                <div className={styles.brandIcon}>
                  <Icon
                    name={`cc/${paymentMethod.brand}`}
                    width={38}
                    height={26}
                  />
                </div>
              </div>
            )}
          </>
        )}
        {isProcessing ? (
          <div
            className={styles.confirmBtn}
            style={{
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            <Icon name="loading-anim-2" width={24} height={24} />
          </div>
        ) : (
          <>
            <button
              type="submit"
              className={styles.confirmBtn}
              onClick={(e) => {
                handleBuyCourse(e);
              }}
              disabled={!isValidInput || isProcessing}
            >
              コースを購入する
            </button>
          </>
        )}
        <PolicyDescription />
      </div>
    );
  };

  const handleClose = () => {
    setOpenCoursePaywall(false);
  };
  if (!openCoursePaywall) return <></>;
  if (courseInfo.is_owner) return <></>;
  if (courseInfo.is_purchased) return <></>;

  return (
    <Dialog
      open={openCoursePaywall}
      onClose={() => {
        if (!isProcessing) {
          handleClose();
        }
      }}
      maxWidth="lg"
      scroll="body"
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      fullScreen={isMobile}
      PaperProps={{
        style: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <div className={styles.dialogWrapper}>
        {!isMobile && (
          <div className={styles.closeBtn}>
            <Icon
              name="close"
              width={28}
              height={28}
              onClick={!isProcessing ? handleClose : undefined}
            />
          </div>
        )}
        <div className={styles.container}>
          {isMobile && (
            <div className={styles.closeBtn}>
              <Icon
                name="close"
                width={28}
                height={28}
                onClick={!isProcessing ? handleClose : undefined}
              />
            </div>
          )}
          <CoursePreview />
          {paymentInfo()}
        </div>
      </div>
      {teenAgePaymentErrorReason && (
        <TeenagePaymentAlertPopup
          reason={teenAgePaymentErrorReason}
          setReason={setTeenAgePaymentErrorReason}
        />
      )}
      {!!stripe3DSUrl && (
        <Dialog open={!!stripe3DSUrl} maxWidth="lg" scroll="body">
          <div className={styles.stripe3DSCheckDialog}>
            <iframe src={stripe3DSUrl} width={600} height={400}></iframe>
          </div>
        </Dialog>
      )}
    </Dialog>
  );
}
